import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { wsManager } from "../services/WebSocketData";
import { getOrderHistory, getActiveOrders, getCurrentUser, getBalances } from "../services/api";
import "../styles/OrderHistory.css";

const FETCH_THROTTLE_MS = 10000;
const GROUP_OPTIONS = [
  { key: "day", label: "Día" },
  { key: "week", label: "Semana" },
  { key: "quincena", label: "Quincena" },
  { key: "month", label: "Mes" },
];

const MONTHS_ES = [
  "Ene", "Feb", "Mar", "Abr", "May", "Jun",
  "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
];

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toTimestamp = (value) => {
  if (!value) return 0;
  const str = String(value);
  const timestamp = new Date(str.endsWith("Z") ? str : str + "Z").getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const formatCurrency = (value, digits = 2, signed = false) => {
  const numeric = toNumber(value);
  if (numeric === null) return "N/A";
  const sign = signed && numeric > 0 ? "+" : "";
  return `${sign}$${numeric.toFixed(digits)}`;
};

const formatPercent = (value, digits = 2) => {
  const numeric = toNumber(value);
  if (numeric === null) return "N/A";
  const sign = numeric > 0 ? "+" : "";
  return `${sign}${numeric.toFixed(digits)}%`;
};

const startOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const periodLabel = (startTs, group) => {
  const d = new Date(startTs);
  if (group === "day") {
    return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
  }
  if (group === "week") {
    return `Semana del ${d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit" })}`;
  }
  if (group === "quincena") {
    const day = d.getDate();
    const month = MONTHS_ES[d.getMonth()];
    const year = d.getFullYear();
    if (day <= 15) return `01-15 ${month} ${year}`;
    return `16-${new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()} ${month} ${year}`;
  }
  return `${MONTHS_ES[d.getMonth()]} ${d.getFullYear()}`;
};

const getPeriodStart = (ts, group) => {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  if (group === "day") {
    return d.getTime();
  }
  if (group === "week") {
    return startOfWeek(d).getTime();
  }
  if (group === "quincena") {
    if (d.getDate() <= 15) {
      return new Date(d.getFullYear(), d.getMonth(), 1).getTime();
    }
    return new Date(d.getFullYear(), d.getMonth(), 16).getTime();
  }
  return new Date(d.getFullYear(), d.getMonth(), 1).getTime();
};

const getInitialRange = () => {
  const now = new Date();
  const from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { desde: fmt(from), hasta: fmt(now) };
};

const calculateRows = (orders, group, desde, hasta, currentEquity) => {
  const fromTs = desde ? new Date(`${desde}T00:00:00Z`).getTime() : 0;
  const toTs = hasta ? new Date(`${hasta}T23:59:59.999Z`).getTime() : Infinity;

  // Equity real de la cuenta: base fija de referencia para los %.
  const base = currentEquity > 0 ? currentEquity : 0;

  const filtered = (Array.isArray(orders) ? orders : [])
    .filter((order) => {
      const ts = toTimestamp(order?.sell_timestamp || order?.fecha_venta);
      return ts > 0 && ts >= fromTs && ts <= toTs;
    })
    .sort((a, b) => {
      const aTs = toTimestamp(a?.sell_timestamp || a?.fecha_venta);
      const bTs = toTimestamp(b?.sell_timestamp || b?.fecha_venta);
      return aTs - bTs;
    });

  const buckets = new Map();
  filtered.forEach((order) => {
    const ts = toTimestamp(order?.sell_timestamp || order?.fecha_venta);
    const start = getPeriodStart(ts, group);
    const current = buckets.get(start) || { pnl: 0, count: 0 };
    current.pnl += toNumber(order?.p_l) ?? 0;
    current.count += 1;
    buckets.set(start, current);
  });

  const pctOf = (value) => (base > 0 ? (value / base) * 100 : null);

  let acumulado = 0;
  const rows = Array.from(buckets.entries())
    .map(([start, data]) => {
      acumulado += data.pnl;
      return {
        start,
        label: periodLabel(start, group),
        count: data.count,
        pnl: data.pnl,
        capital: base,
        capitalFinal: base + acumulado,
        pct: pctOf(data.pnl),
        acumulado,
        acumuladoPct: pctOf(acumulado),
      };
    })
    .sort((a, b) => a.start - b.start);

  const totals = rows.reduce(
    (acc, row) => {
      acc.count += row.count;
      acc.pnl += row.pnl;
      return acc;
    },
    { count: 0, pnl: 0 }
  );
  totals.capital = base;
  totals.capitalFinal = base + totals.pnl;
  totals.pct = pctOf(totals.pnl);
  totals.acumulado = acumulado;
  totals.acumuladoPct = pctOf(acumulado);

  return { rows, totals };
};

const Rendimientos = ({ refreshTrigger = 0, manualRefreshTrigger = 0 }) => {
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [group, setGroup] = useState("day");
  const initialRange = useRef(getInitialRange());
  const [desde, setDesde] = useState(initialRange.current.desde);
  const [hasta, setHasta] = useState(initialRange.current.hasta);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [currentEquity, setCurrentEquity] = useState(0);
  const lastFetchRef = useRef(0);

  const fetchCurrentEquity = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      const profile = currentUser?.data?.user || currentUser?.data?.profile || {};
      const tradeMode = String(profile?.trade_mode || "SIM").toUpperCase();
      let equity = 0;
      if (tradeMode === "SIM") {
        equity = toNumber(profile?.sim_balance) ?? 0;
        let locked = 0;
        const active = await getActiveOrders();
        (Array.isArray(active) ? active : []).forEach((order) => {
          const buyPrice = toNumber(order?.precio_compra) ?? 0;
          const qty = toNumber(order?.cantidad) ?? 0;
          locked += buyPrice * qty;
        });
        equity += locked;
      } else {
        try {
          const balances = await getBalances();
          const data = Array.isArray(balances) ? { balances } : balances || {};
          const totalEquity = toNumber(data?.total_usdt) ?? 0;
          if (totalEquity > 0) {
            equity = totalEquity;
          } else {
            const list = Array.isArray(data.balances) ? data.balances : [];
            list.forEach((b) => {
              if (String(b?.coin || "").toUpperCase() === "USDT") {
                equity += toNumber(b?.free) ?? 0;
              }
            });
          }
        } catch (e) {
          /* ignore */
        }
      }

      setCurrentEquity(equity);
    } catch (err) {
      console.error("❌ Error obteniendo capital actual:", err);
    }
  }, []);

  const fetchOrders = useCallback(async ({ showLoader = false } = {}) => {
    if (showLoader) setLoading(true);
    try {
      const response = await getOrderHistory("");
      let orders = [];
      if (Array.isArray(response)) {
        orders = response;
      } else if (response && Array.isArray(response.orders)) {
        orders = response.orders;
      } else if (response && Array.isArray(response.data)) {
        orders = response.data;
      }
      setAllOrders(Array.isArray(orders) ? orders : []);
      setError(null);
      setLastUpdate(new Date());
    } catch (err) {
      console.error("❌ Error cargando rendimientos:", err);
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        err?.message ||
        "No se pudieron cargar los rendimientos";
      setError(message);
    } finally {
      if (showLoader) setLoading(false);
    }
  }, []);

  const fetchOrdersThrottled = useCallback(
    ({ showLoader = false } = {}) => {
      if (typeof document !== "undefined" && document.hidden) return;
      const now = Date.now();
      if (now - lastFetchRef.current < FETCH_THROTTLE_MS) return;
      lastFetchRef.current = now;
      fetchOrders({ showLoader });
    },
    [fetchOrders]
  );

  useEffect(() => {
    fetchOrders({ showLoader: true });
    fetchCurrentEquity();
    const unsubscribe = wsManager.subscribe((data) => {
      if (
        data?.type === "order_update" ||
        data?.type === "compra" ||
        data?.type === "venta"
      ) {
        fetchOrdersThrottled({ showLoader: false });
        fetchCurrentEquity();
      }
    });
    return () => unsubscribe?.();
  }, [fetchOrders, fetchOrdersThrottled, fetchCurrentEquity]);

  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchOrdersThrottled({ showLoader: false });
      fetchCurrentEquity();
    }
  }, [refreshTrigger, fetchOrdersThrottled, fetchCurrentEquity]);

  useEffect(() => {
    if (manualRefreshTrigger > 0) {
      lastFetchRef.current = Date.now();
      fetchOrders({ showLoader: false });
      fetchCurrentEquity();
    }
  }, [manualRefreshTrigger, fetchOrders, fetchCurrentEquity]);

  const closedOrders = useMemo(() => {
    return (Array.isArray(allOrders) ? allOrders : []).filter((order) => {
      const status = String(order?.status || "OPEN").toUpperCase().trim();
      return (
        status === "CLOSED" ||
        status === "CERRADA" ||
        status === "CLOSE" ||
        status === "SELL" ||
        order?.fecha_venta ||
        order?.sell_timestamp
      );
    });
  }, [allOrders]);

  const { rows, totals } = useMemo(
    () => calculateRows(closedOrders, group, desde, hasta, currentEquity),
    [closedOrders, group, desde, hasta, currentEquity]
  );

  if (loading && closedOrders.length === 0) {
    return (
      <div className="order-history history-loading">
        <div className="history-spinner" />
        <p>⏳ Cargando rendimientos...</p>
      </div>
    );
  }

  return (
    <div className="order-history">
      <header className="history-header">
        <div>
          <h2 className="history-title">📈 Rendimientos</h2>
          <p className="history-subtitle">Resultados agrupados por periodo con acumulado</p>
          {currentEquity > 0 && (
            <p className="history-subtitle">Equity real de la cuenta: {formatCurrency(currentEquity, 2)}</p>
          )}
        </div>

        <div className="history-meta">
          <span>🕒 {lastUpdate ? `Actualizado ${lastUpdate.toLocaleTimeString()}` : "Sin actualización"}</span>
          <span>🧮 {rows.length} periodos</span>
        </div>
      </header>

      {error && <div className="history-error">❌ {error}</div>}

      <section className="history-stats-grid">
        <article className="history-stat-card">
          <span className="history-stat-label">🗓️ Órdenes</span>
          <strong className="history-stat-value">{totals.count}</strong>
        </article>

        <article className={`history-stat-card ${totals.pnl >= 0 ? "tone-positive" : "tone-negative"}`}>
          <span className="history-stat-label">💰 P&L</span>
          <strong className={`history-stat-value ${totals.pnl >= 0 ? "tone-positive" : "tone-negative"}`}>
            {formatCurrency(totals.pnl, 2, true)}
          </strong>
        </article>

        <article className={`history-stat-card ${totals.pct >= 0 ? "tone-positive" : "tone-negative"}`}>
          <span className="history-stat-label">📊 Rendimiento</span>
          <strong className={`history-stat-value ${totals.pct >= 0 ? "tone-positive" : "tone-negative"}`}>
            {formatPercent(totals.pct)}
          </strong>
        </article>

        <article className={`history-stat-card ${totals.acumulado >= 0 ? "tone-positive" : "tone-negative"}`}>
          <span className="history-stat-label">🧮 Acumulado</span>
          <strong className={`history-stat-value ${totals.acumulado >= 0 ? "tone-positive" : "tone-negative"}`}>
            {formatCurrency(totals.acumulado, 2, true)}
          </strong>
          <small className="history-stat-foot">{formatPercent(totals.acumuladoPct)}</small>
        </article>
      </section>

      <section className="history-toolbar">
        <div className="history-filter-group" role="tablist" aria-label="Agrupación de rendimientos">
          {GROUP_OPTIONS.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`history-filter-btn ${group === item.key ? "active" : ""}`}
              onClick={() => setGroup(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="rendimientos-range">
          <label>
            Desde
            <input
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              className="rendimientos-date-input"
            />
          </label>
          <label>
            Hasta
            <input
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              className="rendimientos-date-input"
            />
          </label>

          <button
            type="button"
            className={`history-refresh-btn ${loading ? "loading" : ""}`}
            onClick={() => fetchOrders({ showLoader: true })}
            disabled={loading}
          >
            {loading ? "⏳" : "🔄"} Recargar
          </button>
        </div>
      </section>

      <div className="history-table-wrap">
        <table className="history-table">
          <thead>
            <tr>
              <th className="align-center">Periodo</th>
              <th className="align-center">Órdenes</th>
              <th className="align-center">Capital inicial</th>
              <th className="align-center">Capital final</th>
              <th className="align-center">P&L</th>
              <th className="align-center">Acumulado</th>
              <th className="align-center">%</th>
              <th className="align-center">% Acumulado</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row) => (
              <tr
                key={row.start}
                className={`history-row ${row.pnl > 0 ? "win" : row.pnl < 0 ? "loss" : "flat"}`}
              >
                <td className="history-symbol-cell align-center">{row.label}</td>
                <td className="align-center">{row.count}</td>
                <td className="align-center muted">{row.capital > 0 ? formatCurrency(row.capital, 2) : "N/A"}</td>
                <td className="align-center muted">{row.capitalFinal > 0 ? formatCurrency(row.capitalFinal, 2) : "N/A"}</td>
                <td className={`align-center ${row.pnl > 0 ? "tone-positive" : row.pnl < 0 ? "tone-negative" : "tone-warning"}`}>
                  {formatCurrency(row.pnl, 2, true)}
                </td>
                <td className={`align-center ${row.acumulado > 0 ? "tone-positive" : row.acumulado < 0 ? "tone-negative" : "tone-warning"}`}>
                  {formatCurrency(row.acumulado, 2, true)}
                </td>
                <td className={`align-center ${row.pct > 0 ? "tone-positive" : row.pct < 0 ? "tone-negative" : "tone-warning"}`}>
                  {formatPercent(row.pct)}
                </td>
                <td className={`align-center ${row.acumuladoPct > 0 ? "tone-positive" : row.acumuladoPct < 0 ? "tone-negative" : "tone-warning"}`}>
                  {formatPercent(row.acumuladoPct)}
                </td>
              </tr>
            ))}
          </tbody>

          {rows.length > 0 && (
            <tfoot>
              <tr className="history-row">
                <td className="history-symbol-cell align-center">Total</td>
                <td className="align-center">{totals.count}</td>
                <td className="align-center muted">{totals.capital > 0 ? formatCurrency(totals.capital, 2) : "N/A"}</td>
                <td className="align-center muted">{totals.capitalFinal > 0 ? formatCurrency(totals.capitalFinal, 2) : "N/A"}</td>
                <td className={`align-center ${totals.pnl >= 0 ? "tone-positive" : "tone-negative"}`}>
                  {formatCurrency(totals.pnl, 2, true)}
                </td>
                <td className={`align-center ${totals.acumulado >= 0 ? "tone-positive" : "tone-negative"}`}>
                  {formatCurrency(totals.acumulado, 2, true)}
                </td>
                <td className={`align-center ${totals.pct >= 0 ? "tone-positive" : "tone-negative"}`}>
                  {formatPercent(totals.pct)}
                </td>
                <td className={`align-center ${totals.acumuladoPct >= 0 ? "tone-positive" : "tone-negative"}`}>
                  {formatPercent(totals.acumuladoPct)}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="history-cards">
        {rows.map((row) => (
          <article
            key={row.start}
            className={`history-card ${row.pnl > 0 ? "win" : row.pnl < 0 ? "loss" : "flat"}`}
          >
            <div className="history-card-header">
              <div>
                <span className="history-card-symbol">{row.label}</span>
                <span className="history-card-date">{row.count} órdenes </span>
              </div>
              <div
                className={`history-card-pnl ${row.pnl > 0 ? "tone-positive" : row.pnl < 0 ? "tone-negative" : "tone-warning"}`}
              >
                <span>{formatCurrency(row.pnl, 2, true)}</span>
                <small>{formatPercent(row.pct)}</small>
              </div>
            </div>

            <div className="history-card-grid">
              <div className="history-card-kv">
                <span>Capital inicial</span>
                <strong>{row.capital > 0 ? formatCurrency(row.capital, 2) : "N/A"}</strong>
              </div>
              <div className="history-card-kv">
                <span>Capital final</span>
                <strong>{row.capitalFinal > 0 ? formatCurrency(row.capitalFinal, 2) : "N/A"}</strong>
              </div>
              <div className="history-card-kv">
                <span>Acumulado</span>
                <strong className={row.acumulado > 0 ? "tone-positive" : row.acumulado < 0 ? "tone-negative" : "tone-warning"}>
                  {formatCurrency(row.acumulado, 2, true)}
                </strong>
              </div>
              <div className="history-card-kv">
                <span>% Acumulado</span>
                <strong>{formatPercent(row.acumuladoPct)}</strong>
              </div>
            </div>
          </article>
        ))}

        {rows.length > 0 && (
          <article className={`history-card ${totals.pnl > 0 ? "win" : totals.pnl < 0 ? "loss" : "flat"}`}>
            <div className="history-card-header">
              <div>
                <span className="history-card-symbol">Total</span>
                <span className="history-card-date">{totals.count} órdenes</span>
              </div>
              <div
                className={`history-card-pnl ${totals.pnl > 0 ? "tone-positive" : totals.pnl < 0 ? "tone-negative" : "tone-warning"}`}
              >
                <span>{formatCurrency(totals.pnl, 2, true)}</span>
                <small>{formatPercent(totals.pct)}</small>
              </div>
            </div>

            <div className="history-card-grid">
              <div className="history-card-kv">
                <span>Capital inicial</span>
                <strong>{totals.capital > 0 ? formatCurrency(totals.capital, 2) : "N/A"}</strong>
              </div>
              <div className="history-card-kv">
                <span>Capital final</span>
                <strong>{totals.capitalFinal > 0 ? formatCurrency(totals.capitalFinal, 2) : "N/A"}</strong>
              </div>
              <div className="history-card-kv">
                <span>Acumulado</span>
                <strong className={totals.acumulado > 0 ? "tone-positive" : totals.acumulado < 0 ? "tone-negative" : "tone-warning"}>
                  {formatCurrency(totals.acumulado, 2, true)}
                </strong>
              </div>
              <div className="history-card-kv">
                <span>% Acumulado</span>
                <strong>{formatPercent(totals.acumuladoPct)}</strong>
              </div>
            </div>
          </article>
        )}
      </div>

      {rows.length === 0 && (
        <div className="history-empty">
          <p>📭 Sin órdenes cerradas en el rango de fechas seleccionado</p>
        </div>
      )}
    </div>
  );
};

export default React.memo(Rendimientos);
